function shown(value, fallback = true) {
  if (value === undefined || value === null || value === "") return fallback;
  return value !== false && value !== "false";
}

function text(value, fallback) {
  const next = typeof value === "string" ? value.trim() : "";
  return next || fallback;
}

function oneOf(value, allowed, fallback) {
  const next = String(value || "");
  return allowed.includes(next) ? next : fallback;
}

export function productPageSettings(settings = {}) {
  return {
    mobileAlign: oneOf(settings.productMobileAlign, ["center", "left"], "center"),
    mobileBuyFull: shown(settings.productMobileBuyFull, true),
    // Desktop layout. Defaults match the page before these settings existed.
    galleryWidth: oneOf(settings.productGalleryWidth, ["half", "large", "wide"], "half"),
    imageSize: oneOf(settings.productImageSize, ["standard", "large", "fill"], "standard"),
    thumbs: oneOf(settings.productThumbs, ["bottom", "left"], "bottom"),
    imageFrame: oneOf(settings.productImageFrame, ["card", "plain"], "card"),
    pageWidth: oneOf(settings.productPageWidth, ["standard", "wide"], "standard"),
    infoAlign: oneOf(settings.productInfoAlign, ["left", "center"], "left"),
    infoStyle: oneOf(settings.productInfoStyle, ["plain", "box"], "plain"),
    showBreadcrumbs: shown(settings.productShowBreadcrumbs, true),
    showRating: shown(settings.productShowRating, true),
    showBadges: shown(settings.productShowBadges, true),
    imageZoom: oneOf(settings.productImageZoom, ["magnify", "gentle", "off"], "magnify"),
    // Words and parts of the buy box. Defaults are the page's original text.
    titleSize: oneOf(settings.productTitleSize, ["small", "medium", "large"], "medium"),
    priceColor: text(settings.productPriceColor, ""),
    showCategory: shown(settings.productShowCategory, true),
    showStock: shown(settings.productShowStock, true),
    addLabel: text(settings.productAddLabel, "Add to Cart"),
    showBuyNow: shown(settings.productShowBuyNow, true),
    buyLabel: text(settings.productBuyLabel, "Buy Now"),
    showTrust: shown(settings.productShowTrust, true),
    trustAuthentic: text(settings.productTrustAuthentic, "100% Authentic"),
    trustReturns: text(settings.productTrustReturns, "7 Days Returns"),
    descriptionTitle: text(settings.productDescriptionTitle, "Product Description"),
  };
}

// Parts of the product page below the gallery and buy box, in the order the
// theme lists them. "product_reviews" and "recently_viewed" are built into the
// product page; the others reuse homepage sections (src/themes/sections).
export const PRODUCT_PAGE_SECTION_TYPES = [
  { value: "frequently_bought", label: "Frequently bought together and bundles" },
  { value: "product_reviews", label: "Customer reviews" },
  { value: "recently_viewed", label: "Recently viewed" },
  { value: "rich_text", label: "Rich text" },
  { value: "trust_strip", label: "Trust badges" },
];

// Frequently bought together first, then the page's original parts.
export const DEFAULT_PRODUCT_PAGE_SECTIONS = [
  { id: "pp_fbt", type: "frequently_bought", enabled: true },
  { id: "pp_reviews", type: "product_reviews", enabled: true },
  { id: "pp_recent", type: "recently_viewed", enabled: true },
];

export function productPageSections(settings = {}) {
  const list = Array.isArray(settings.productPageSections) ? settings.productPageSections : DEFAULT_PRODUCT_PAGE_SECTIONS;
  const known = PRODUCT_PAGE_SECTION_TYPES.map((item) => item.value);
  return list.filter((item) => item && known.includes(item.type) && shown(item.enabled, true));
}

export const PRODUCT_PAGE_SECTIONS_FIELD = {
  key: "productPageSections",
  label: "Sections below the product",
  type: "blocks",
  itemName: "section",
  max: 8,
  hint: "Shown under the photos and Buy Now button, top to bottom. Reorder, hide, or add sections.",
  defaultValue: DEFAULT_PRODUCT_PAGE_SECTIONS,
  summary: { title: "type", text: "heading" },
  defaultItem: { type: "rich_text", enabled: true, heading: "", content: "", alignment: "center" },
  fields: [
    { key: "type", label: "Section", type: "select", options: PRODUCT_PAGE_SECTION_TYPES },
    { key: "enabled", label: "Show on the product page", type: "toggle" },
    { key: "heading", label: "Heading", type: "text", showWhen: { key: "type", value: "rich_text" } },
    { key: "content", label: "Text", type: "textarea", showWhen: { key: "type", value: "rich_text" } },
    {
      key: "alignment",
      label: "Alignment",
      type: "select",
      options: [
        { value: "center", label: "Center" },
        { value: "left", label: "Left" },
      ],
      showWhen: { key: "type", value: "rich_text" },
    },
  ],
};

// Bootstrap columns (out of 12) for the photo area at each width.
export const PRODUCT_GALLERY_COLUMNS = { half: 6, large: 7, wide: 8 };

const choice = (key, label, options) => ({ key, label, type: "select", options });

// Layout fields for Theme → Product page.
export const PRODUCT_LAYOUT_FIELDS = [
  choice("productGalleryWidth", "Photo area width", [
    { value: "half", label: "Half the page" },
    { value: "large", label: "Large (about 60%)" },
    { value: "wide", label: "Extra large (about two thirds)" },
  ]),
  choice("productImageSize", "Photo size", [
    { value: "standard", label: "Standard" },
    { value: "large", label: "Large" },
    { value: "fill", label: "Fill the photo area (square)" },
  ]),
  choice("productThumbs", "Small photos", [
    { value: "bottom", label: "Under the main photo" },
    { value: "left", label: "Down the left side" },
  ]),
  choice("productImageZoom", "Zoom when the mouse is over the photo", [
    { value: "magnify", label: "Magnify where the mouse points" },
    { value: "gentle", label: "Gentle zoom" },
    { value: "off", label: "Off" },
  ]),
  choice("productImageFrame", "Photo frame", [
    { value: "card", label: "Rounded card with shadow" },
    { value: "plain", label: "Plain, no frame" },
  ]),
  choice("productPageWidth", "Page width", [
    { value: "standard", label: "Standard" },
    { value: "wide", label: "Wide (uses more of large screens)" },
  ]),
  choice("productInfoAlign", "Product details alignment", [
    { value: "left", label: "Left" },
    { value: "center", label: "Center" },
  ]),
  choice("productInfoStyle", "Product details style", [
    { value: "plain", label: "Plain" },
    { value: "box", label: "Inside a bordered box" },
  ]),
  { key: "productShowBreadcrumbs", label: "Show breadcrumbs (Home / Collections / …)", type: "toggle" },
  { key: "productShowRating", label: "Show star rating", type: "toggle" },
  { key: "productShowBadges", label: "Show Featured and Trending badges", type: "toggle" },
];

// Text and parts of the buy box, for Theme → Product page.
export const PRODUCT_TEXT_FIELDS = [
  choice("productTitleSize", "Product name size", [
    { value: "small", label: "Small" },
    { value: "medium", label: "Medium" },
    { value: "large", label: "Large" },
  ]),
  { key: "productPriceColor", label: "Price color", type: "color", hint: "Leave empty to use the theme color." },
  { key: "productShowCategory", label: "Show the category name above the product name", type: "toggle" },
  { key: "productShowStock", label: "Show stock (In stock / Out of stock)", type: "toggle" },
  { key: "productAddLabel", label: "Add to cart button text", type: "text", placeholder: "Add to Cart" },
  { key: "productShowBuyNow", label: "Show the Buy Now button", type: "toggle" },
  { key: "productBuyLabel", label: "Buy Now button text", type: "text", placeholder: "Buy Now" },
  { key: "productShowTrust", label: "Show the delivery, authentic, and returns strip", type: "toggle" },
  { key: "productTrustAuthentic", label: "Second strip item", type: "text", placeholder: "100% Authentic" },
  { key: "productTrustReturns", label: "Third strip item", type: "text", placeholder: "7 Days Returns" },
  { key: "productDescriptionTitle", label: "Description heading", type: "text", placeholder: "Product Description" },
];
